import { bind } from 'bind-event-listener';

import { AbstractMentionResource } from '@atlaskit/mention/abstract-mention-resource';
import { isTeamMention } from '@atlaskit/mention/is-team-mention';
import type {
	MentionDescription,
	MentionDisabledState,
	MentionDisabledStateInput,
	MentionNameDetails,
	MentionPlaceholder,
	MentionStats,
	MentionContextIdentifier,
} from '@atlaskit/mention/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { filterAgentsClientSide } from '@atlassian/conversation-assistant-agent/controllers/mention-agents/filter-agents-client-side';
import type {
	MentionAgent,
	MentionAgentSource,
} from '@atlassian/conversation-assistant-agent/services/mention-agents/types';
import {
	DEFAULT_AGENT_CREATOR_TYPE,
	DEFAULT_AGENT_UUID,
} from '@atlassian/conversation-assistant-utils/default-agent';
import type SmartMentionResource from '@atlassian/smart-mention-resource/services/smart-mention-resource';

import { type MentionAnalyticsCallback } from './mention-analytics-event';
import { queryMatchesLabel } from './query-matches-label';
import { buildRovoMention } from './rovo-mention';

const AGENT_DROPDOWN_LIMIT = 5;
const EMPTY_QUERY_PEOPLE_LIMIT = 5;
const TYPED_QUERY_PEOPLE_LIMIT = 10;

/**
 * Number of loading rows shown in the Agents section while agents resolve.
 */
const AGENT_LOADING_PLACEHOLDER_COUNT = 2;

/**
 * Prefix for placeholder ids. Each placeholder needs a unique id because the
 * typeahead keys list rows by id — so multiple placeholders can be rendered
 * at once. Detection is by the `isPlaceholder` flag (type-safe via the
 * `MentionDescription` discriminated union), never by matching this string.
 */
const AGENT_LOADING_PLACEHOLDER_ID_PREFIX = '__rovo-agents-loading-';

/**
 * Builds a non-selectable loading placeholder for the agents slot.
 * `appType: 'agent'` / `userType: 'APP'` so the typeahead files it under
 * Agents; `isPlaceholder` drives the skeleton render in `MentionItem` and
 * the no-op selection guards.
 */
const createAgentLoadingPlaceholder = (index: number): MentionPlaceholder => ({
	id: `${AGENT_LOADING_PLACEHOLDER_ID_PREFIX}${index}__`,
	isPlaceholder: true,
	placeholderType: 'loading',
	userType: 'APP',
	appType: 'agent',
});

const isLoadingPlaceholder = (mention: MentionDescription): boolean =>
	mention.isPlaceholder === true;

const getDefaultAgentMentionNodeId = (agent: MentionAgent): string => agent.id;

const agentToMentionDescription = (
	agent: MentionAgent,
	getAgentMentionNodeId: (agent: MentionAgent) => string | null,
): MentionDescription | null => {
	const mentionNodeId = getAgentMentionNodeId(agent);
	if (!mentionNodeId) {
		return null;
	}

	return {
		id: mentionNodeId,
		name: agent.name,
		mentionName: agent.name,
		avatarUrl: agent.avatarUrl ?? undefined,
		userType: 'APP',
		appType: 'agent',
	};
};

/**
 * Public shape passed back when the user selects an agent row.
 * Consumers depend on this narrow shape (id + name).
 */
export type SelectedAgentRef = {
	id: string;
	name: string;
	identityAccountId?: string;
	creatorType?: string;
	/** Identity picture already shown on the mention row, when one exists. */
	avatarUrl?: string;
};

type OnAgentSelected = (agent: SelectedAgentRef) => void;
type SelectedAgentIdsGetter = () => readonly string[];

export type RovoChatMentionResourceConfig = {
	/**
	 * Pre-built `SmartMentionResource` for the people typeahead. Hosts
	 * that already own an instance (Townsquare's singleton) pass it in
	 * directly. The default path goes through
	 * `createRovoChatMentionResource()` which builds one internally.
	 */
	peopleResource: SmartMentionResource;
	/**
	 * Optional — when omitted (or `null`), the resource degrades to a
	 * people-only provider. Used when the agent-selection feature gate
	 * is off or no Relay environment is available.
	 */
	agentSource?: MentionAgentSource | null;
	onAgentSelected?: OnAgentSelected;
	getSelectedAgentIds?: SelectedAgentIdsGetter;
	/** Returns a receiver-compatible ADF node ID, or null to omit an agent that cannot be serialized safely. */
	getAgentMentionNodeId?: (agent: MentionAgent) => string | null;
	disabledAgentTooltip?: string;
	onAgentMentionDestroyed?: (agentId: string) => void;
	// True when the conversation is bound to a dedicated (non-default) agent
	isDedicatedAgentChat?: boolean;
	// i18n label for the synthetic "Rovo" mention row; the row only appears when provided.
	rovoMentionLabel?: string;
	/**
	 * Idle debounce (ms) applied only to the server-side agent
	 * `searchByName` round trip, so we don't issue a Relay request on
	 * every keystroke. When omitted, the agent search fires immediately
	 * (no debounce). Mirrors the `debounceTime` naming used by
	 * `@atlaskit/mention`'s `MentionResourceConfig`.
	 */
	debounceTime?: number;
	/**
	 * Opts this combined people-and-agent resource into menu load timing. The resource also checks
	 * `rovo_chat_mention_performance_analytics`, so callers cannot bypass the rollout gate.
	 */
	collectPerformanceAnalytics?: boolean;
};

/**
 * Prefix for the per-instance subscription key (see `innerSubscriptionKey`).
 * The people resource keys listeners in a `Map`, so instances sharing one
 * `SmartMentionResource` need distinct keys to avoid evicting each other.
 */
const INNER_SUBSCRIPTION_KEY_PREFIX = 'rovo-chat-mention-resource:people';
let innerSubscriptionKeyCounter = 0;

const isAgentMention = (mention: MentionDescription): boolean =>
	mention.userType === 'APP' && mention.appType === 'agent';

const isPersonMention = (mention: MentionDescription): boolean => mention.userType === 'DEFAULT';
const isTeamMentionResult = (mention: MentionDescription): boolean =>
	isTeamMention(mention) === true;

const ROVO_CANONICAL_QUERY = 'Rovo';

const queryMatchesRovoMention = (query: string, label: string): boolean =>
	queryMatchesLabel(query, label) || queryMatchesLabel(query, ROVO_CANONICAL_QUERY);

/**
 * Mention provider for Rovo Chat. Holds an injected
 * `SmartMentionResource` for people and an optional `MentionAgentSource`
 * for agents. When an agent source is present, each `filter()` fires both
 * searches in parallel; the dropdown renders people as soon as URS resolves
 * and re-renders with agents appended when they arrive. Without an agent
 * source, the resource forwards the native people result set unchanged while
 * retaining the same analytics and lifecycle seam. The first empty-query
 * result is cached so backspacing back to `@` restores the original 5+5
 * dropdown instantly.
 */
export class RovoChatMentionResource extends AbstractMentionResource {
	/** Unique per-instance key for our listener on the shared people resource. */
	private readonly innerSubscriptionKey = `${INNER_SUBSCRIPTION_KEY_PREFIX}:${++innerSubscriptionKeyCounter}`;
	private readonly peopleResource: SmartMentionResource;
	private readonly agentSource: MentionAgentSource | null;
	private readonly onAgentSelected: OnAgentSelected | undefined;
	private readonly getSelectedAgentIds: SelectedAgentIdsGetter | undefined;
	private readonly getAgentMentionNodeId: (agent: MentionAgent) => string | null;
	private disabledAgentTooltip: string | undefined;
	private readonly onAgentMentionDestroyed: ((agentId: string) => void) | undefined;
	private readonly isDedicatedAgentChat: boolean;
	private readonly rovoMentionLabel: string | undefined;
	private mentionAnalyticsCallback: MentionAnalyticsCallback | undefined;
	/** Notified when the active agent selection changes so rendered chips re-evaluate. */
	private readonly disabledStateListeners = new Set<() => void>();

	private currentAbort: AbortController | null = null;

	/** Idle debounce (ms) for the server-side agent search, sourced from
	 *  `config.debounceTime`. `undefined` means fire immediately. */
	private readonly debounceTime: number | undefined;
	private readonly collectPerformanceAnalytics: boolean;

	/** Idle timer for the debounced server-side agent search. Tracked
	 *  alongside `currentAbort` so it can be cleared on the next keystroke
	 *  and on dispose. */
	private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

	/** Most recent search state. People and agent cells are written
	 *  independently as each resolves; `tryEmit()` renders the cells
	 *  tagged with `currentQuery`. */
	private currentQuery = '';
	private currentPeople: {
		mentions: MentionDescription[];
		stats: MentionStats | undefined;
	} | null = null;
	private currentAgents: readonly MentionAgent[] | null = null;
	private currentDisplayedPeople: readonly MentionDescription[] = [];
	private currentDisplayedAgents: readonly MentionAgent[] = [];
	/** UI-facing agent rows, including synthetic Rovo mentions absent from raw agent results. */
	private currentDisplayedAgentMentions: readonly MentionDescription[] = [];

	/** First empty-query result, kept so backspacing back to `@` restores instantly. */
	private emptyPeopleSnapshot: {
		mentions: MentionDescription[];
		stats: MentionStats | undefined;
	} | null = null;
	private emptyAgentsSnapshot: readonly MentionAgent[] | null = null;
	private filterSequence = 0;
	private lastReportedFilterSequence: number | null = null;
	private loadTiming: {
		startedAt: number;
		startedAtEpochMs: number;
		peopleResultsLoadDurationMs?: number;
		agentResultsLoadDurationMs?: number;
	} | null = null;

	constructor(config: RovoChatMentionResourceConfig) {
		super();
		this.peopleResource = config.peopleResource;
		this.agentSource = config.agentSource ?? null;
		this.onAgentSelected = config.onAgentSelected;
		this.getSelectedAgentIds = config.getSelectedAgentIds;
		this.getAgentMentionNodeId = config.getAgentMentionNodeId ?? getDefaultAgentMentionNodeId;
		this.disabledAgentTooltip = config.disabledAgentTooltip;
		this.onAgentMentionDestroyed = config.onAgentMentionDestroyed;
		this.isDedicatedAgentChat = config.isDedicatedAgentChat ?? false;
		this.rovoMentionLabel = config.rovoMentionLabel;
		this.debounceTime = config.debounceTime;
		this.collectPerformanceAnalytics = config.collectPerformanceAnalytics ?? false;

		this.peopleResource.subscribe(
			this.innerSubscriptionKey,
			(mentions, query, stats) => {
				this.onPeopleEmit(mentions, query ?? '', stats);
			},
			(error, query) => {
				if (this.agentSource === null) {
					this._notifyErrorListeners(error, query);
				}
			},
			(info) => {
				if (this.agentSource === null) {
					this._notifyInfoListeners(info);
				}
			},
			(mentions, query) => {
				if (this.agentSource === null) {
					this._notifyAllResultsListeners({
						mentions: [...mentions],
						query: query ?? this.currentQuery,
					});
				}
			},
			(event, actionSubject, action, attributes) => {
				if (this.agentSource === null) {
					this._notifyAnalyticsListeners(event, actionSubject, action, attributes);
				}
			},
		);
	}

	/**
	 * Updates the mention analytics handler without rebuilding this resource.
	 * Pass `undefined` to stop collecting events.
	 */
	setMentionAnalyticsCallback(callback: MentionAnalyticsCallback | undefined): void {
		this.mentionAnalyticsCallback = callback;
	}

	private onPeopleEmit(
		mentions: MentionDescription[],
		query: string,
		stats: MentionStats | undefined,
	): void {
		// Drop stale URS emissions tagged with a query the user has moved past.
		if (query !== this.currentQuery) {
			return;
		}
		if (this.loadTiming && this.loadTiming.peopleResultsLoadDurationMs === undefined) {
			const peopleRequestStartedAt =
				typeof stats?.duration === 'number' ? Date.now() - stats.duration : undefined;
			// Identical consecutive queries have the same callback query. The request duration lets us
			// avoid attributing the earlier request to the latest filter's timing window.
			if (
				peopleRequestStartedAt === undefined ||
				peopleRequestStartedAt >= this.loadTiming.startedAtEpochMs
			) {
				this.loadTiming.peopleResultsLoadDurationMs = Math.round(
					performance.now() - this.loadTiming.startedAt,
				);
			}
		}
		this.currentPeople = { mentions, stats };
		if (this.currentQuery.trim() === '') {
			this.emptyPeopleSnapshot = { mentions, stats };
		}
		this.tryEmit();
	}

	/**
	 * Releases this instance's subscription on the inner people
	 * resource, aborts any in-flight agent fetch, and drops the
	 * disabled-state listeners. After `dispose()` the instance must not
	 * be reused.
	 */
	dispose(): void {
		this.currentAbort?.abort();
		this.currentAbort = null;
		this.clearSearchDebounce();
		this.currentPeople = null;
		this.currentAgents = null;
		this.currentDisplayedPeople = [];
		this.currentDisplayedAgents = [];
		this.currentDisplayedAgentMentions = [];
		this.emptyPeopleSnapshot = null;
		this.emptyAgentsSnapshot = null;
		this.lastReportedFilterSequence = null;
		this.loadTiming = null;
		this.mentionAnalyticsCallback = undefined;
		this.disabledStateListeners.clear();
		// Always release our subscription on the inner people resource, even
		// when the host owns it. Otherwise the wrapper's closure-bound callback
		// stays registered on the host singleton across remounts.
		this.peopleResource.unsubscribe(this.innerSubscriptionKey);
	}

	filter(query?: string, contextIdentifier?: MentionContextIdentifier): void {
		const rawQuery = query ?? '';
		const trimmed = rawQuery.trim();
		const isEmpty = trimmed.length === 0;

		// Abort the previous keystroke's fetches so their `then` handlers bail,
		// and cancel any pending (not-yet-fired) debounced search.
		this.currentAbort?.abort();
		this.clearSearchDebounce();
		const abort = new AbortController();
		this.currentAbort = abort;

		// Reset cells (snapshots persist — they're the stable empty-query default).
		this.currentQuery = rawQuery;
		this.currentPeople = null;
		this.currentAgents = this.agentSource ? null : [];
		this.currentDisplayedPeople = [];
		this.currentDisplayedAgents = [];
		this.filterSequence += 1;
		this.loadTiming =
			this.agentSource &&
			this.collectPerformanceAnalytics &&
			fg('rovo_chat_mention_performance_analytics')
				? { startedAt: performance.now(), startedAtEpochMs: Date.now() }
				: null;

		const restoredEmptySnapshot = isEmpty && this.emptyPeopleSnapshot && this.emptyAgentsSnapshot;
		// Empty-query fast path: restore cached snapshot for instant @-dropdown.
		if (restoredEmptySnapshot) {
			this.currentPeople = this.emptyPeopleSnapshot;
			this.currentAgents = this.emptyAgentsSnapshot;
			if (this.loadTiming) {
				const cachedLoadDurationMs = Math.round(performance.now() - this.loadTiming.startedAt);
				this.loadTiming.peopleResultsLoadDurationMs = cachedLoadDurationMs;
				this.loadTiming.agentResultsLoadDurationMs = cachedLoadDurationMs;
			}
			queueMicrotask(() => {
				if (abort.signal.aborted) {
					return;
				}
				this.tryEmit();
			});
		}

		this.peopleResource.filter(query, contextIdentifier);
		if (restoredEmptySnapshot) {
			return;
		}

		if (this.agentSource) {
			void this.fetchAgents(trimmed, abort.signal).then((agents) => {
				if (abort.signal.aborted) {
					return;
				}
				if (this.loadTiming && this.loadTiming.agentResultsLoadDurationMs === undefined) {
					this.loadTiming.agentResultsLoadDurationMs = Math.round(
						performance.now() - this.loadTiming.startedAt,
					);
				}
				this.currentAgents = agents;
				if (isEmpty) {
					this.emptyAgentsSnapshot = agents;
				}
				this.tryEmit();
			});
		}
	}

	/** Emit current cells to subscribers. Waits for people; agents render below.
	 *  Agent-enabled slicing is 5+5 for empty query and 10+5 for typed queries.
	 *  The people-only path remains transparent to the wrapped resource. */
	private tryEmit(): void {
		if (!this.currentPeople) {
			return;
		}
		const { mentions: people, stats } = this.currentPeople;
		if (this.agentSource === null) {
			this.currentDisplayedPeople = people;
			this.currentDisplayedAgents = [];
			this.currentDisplayedAgentMentions = [];
			const peopleResultCount = people.filter(isPersonMention).length;
			const teamResultCount = people.filter(isTeamMentionResult).length;
			this.reportMentionMenuAnalytics(0, peopleResultCount, teamResultCount);
			this._notifyListeners({ mentions: [...people], query: this.currentQuery }, stats);
			return;
		}
		const isEmpty = this.currentQuery.trim().length === 0;
		const peopleLimit = isEmpty ? EMPTY_QUERY_PEOPLE_LIMIT : TYPED_QUERY_PEOPLE_LIMIT;
		const peopleSlice = people.slice(0, peopleLimit);
		let agents: MentionDescription[];
		let visibleAgents: readonly MentionAgent[] = [];
		if (this.currentAgents) {
			visibleAgents = this.currentAgents;
			agents = visibleAgents.flatMap((agent) => {
				const mention = agentToMentionDescription(agent, this.getAgentMentionNodeId);
				return mention ? [mention] : [];
			});
		} else if (this.agentSource && isExperimentEnabled('rovo_chat_mention_agents')) {
			agents = Array.from({ length: AGENT_LOADING_PLACEHOLDER_COUNT }, (_, index) =>
				createAgentLoadingPlaceholder(index),
			);
		} else {
			agents = [];
		}
		const agentsWithRovoMention = this.withRovoMention(agents);
		const agentsSlice = agentsWithRovoMention.slice(0, AGENT_DROPDOWN_LIMIT);
		this.currentDisplayedPeople = peopleSlice;
		this.currentDisplayedAgentMentions = agentsSlice.filter(
			(mention) => isAgentMention(mention) && !isLoadingPlaceholder(mention),
		);
		this.currentDisplayedAgents = visibleAgents.filter((agent) =>
			this.currentDisplayedAgentMentions.some(
				(mention) => mention.id === this.getAgentMentionNodeId(agent),
			),
		);
		const peopleResultCount = peopleSlice.filter(isPersonMention).length;
		const teamResultCount = peopleSlice.filter(isTeamMentionResult).length;
		this.reportMentionMenuAnalytics(
			this.currentDisplayedAgentMentions.length,
			peopleResultCount,
			teamResultCount,
		);
		this._notifyListeners(
			{ mentions: [...peopleSlice, ...agentsSlice], query: this.currentQuery },
			stats,
		);
	}

	// Prepend the "Rovo" mention row when the gate is on and the user is in an agent context (an
	// agent is mentioned in the input, or the conversation is a dedicated agent chat). In a dedicated
	// agent chat, show it for the initial empty query so it is available before agent results hydrate;
	// otherwise the query must target "Rovo". Deduped against a same-id agent from the source.
	private withRovoMention(agents: MentionDescription[]): MentionDescription[] {
		const label = this.rovoMentionLabel;
		const inAgentContext =
			(this.getSelectedAgentIds?.().length ?? 0) > 0 || this.isDedicatedAgentChat;
		const showForDedicatedAgentChat =
			this.isDedicatedAgentChat && this.currentQuery.trim().length === 0;
		if (
			!isExperimentEnabled('rovo_chat_mention_agents') ||
			!label ||
			!inAgentContext ||
			(!showForDedicatedAgentChat && !queryMatchesRovoMention(this.currentQuery, label))
		) {
			return agents;
		}
		const rovo = buildRovoMention(label);
		return [rovo, ...agents.filter((agent) => agent.id !== rovo.id)];
	}

	private reportMentionMenuAnalytics(
		agentResultCount: number,
		peopleResultCount: number,
		teamResultCount: number,
	): void {
		if (this.currentAgents === null) {
			return;
		}
		if (this.lastReportedFilterSequence === this.filterSequence) {
			return;
		}
		const trimmed = this.currentQuery.trim();
		const timing = this.loadTiming;
		if (
			timing &&
			(timing.peopleResultsLoadDurationMs === undefined ||
				timing.agentResultsLoadDurationMs === undefined)
		) {
			return;
		}
		this.lastReportedFilterSequence = this.filterSequence;
		const loadDurationContext =
			timing?.peopleResultsLoadDurationMs !== undefined &&
			timing.agentResultsLoadDurationMs !== undefined
				? {
						peopleResultsLoadDurationMs: timing.peopleResultsLoadDurationMs,
						agentResultsLoadDurationMs: timing.agentResultsLoadDurationMs,
						totalResultsLoadDurationMs: Math.round(performance.now() - timing.startedAt),
					}
				: {};
		this.mentionAnalyticsCallback?.({
			action: trimmed.length === 0 ? 'opened' : 'searched',
			queryLength: trimmed.length,
			agentResultCount,
			peopleResultCount,
			teamResultCount,
			...loadDurationContext,
		});
	}

	private async fetchAgents(
		trimmed: string,
		signal: AbortSignal,
	): Promise<readonly MentionAgent[]> {
		const source = this.agentSource;
		if (!source) {
			return [];
		}
		try {
			if (trimmed.length === 0) {
				return await source.getAllAgents(signal);
			}
			if (source.canFilterClientSide()) {
				const all = await source.getAllAgents(signal);
				return filterAgentsClientSide(all, trimmed, AGENT_DROPDOWN_LIMIT);
			}
			// Server-side typed search. When a debounce is configured, wait out
			// the idle window first so we don't fire a Relay request on every
			// keystroke; if a newer keystroke arrives the wait is cancelled and
			// this fetch resolves to [] (the newer filter() drives the result).
			if (this.debounceTime !== undefined) {
				const settled = await this.waitForSearchDebounce(this.debounceTime, signal);
				if (!settled) {
					return [];
				}
			}
			const searched = await source.searchByName(trimmed, signal);
			return searched;
		} catch (err) {
			return [];
		}
	}

	/**
	 * Resolves `true` after the idle debounce window, or `false` if the
	 * search is cancelled first (next keystroke / dispose aborts the
	 * signal). Only the server-side search path awaits this.
	 */
	private waitForSearchDebounce(debounceMs: number, signal: AbortSignal): Promise<boolean> {
		return new Promise<boolean>((resolve) => {
			if (signal.aborted) {
				resolve(false);
				return;
			}
			const unbind = bind(signal, {
				type: 'abort',
				listener: () => {
					this.clearSearchDebounce();
					resolve(false);
				},
				options: { once: true },
			});
			this.clearSearchDebounce();
			this.searchDebounceTimer = setTimeout(() => {
				this.searchDebounceTimer = null;
				unbind();
				resolve(!signal.aborted);
			}, debounceMs);
		});
	}

	private clearSearchDebounce(): void {
		if (this.searchDebounceTimer !== null) {
			clearTimeout(this.searchDebounceTimer);
			this.searchDebounceTimer = null;
		}
	}

	isFiltering(query: string): boolean {
		return this.peopleResource.isFiltering?.(query) ?? false;
	}

	shouldHighlightMention(mention: MentionDescription): boolean {
		return this.peopleResource.shouldHighlightMention?.(mention) ?? false;
	}

	supportsMentionNameResolving(): boolean {
		return this.peopleResource.supportsMentionNameResolving?.() ?? false;
	}

	resolveMentionName(id: string): Promise<MentionNameDetails> | MentionNameDetails | undefined {
		return this.peopleResource.resolveMentionName?.(id);
	}

	cacheMentionName(id: string, mentionName: string): void {
		this.peopleResource.cacheMentionName?.(id, mentionName);
	}

	recordMentionSelection(
		mention: MentionDescription,
		_contextIdentifier?: MentionContextIdentifier,
	): void {
		// The loading placeholder is never a real selection — ignore it.
		if (isLoadingPlaceholder(mention)) {
			return;
		}
		// Always notify the inner people resource so people-row analytics
		// and recent-pick state keep working uniformly.
		this.peopleResource.recordMentionSelection?.(mention);

		const queryLength = this.currentQuery.trim().length;
		const displayedPeople = this.currentDisplayedPeople.filter(isPersonMention);
		const displayedTeams = this.currentDisplayedPeople.filter(isTeamMentionResult);
		const peopleResultCount = displayedPeople.length;
		const teamResultCount = displayedTeams.length;
		if (isPersonMention(mention)) {
			const selectedPersonPosition = displayedPeople.findIndex(
				(person) => person.id === mention.id,
			);
			this.mentionAnalyticsCallback?.({
				action: 'selected',
				resultType: 'person',
				position: selectedPersonPosition >= 0 ? selectedPersonPosition : undefined,
				queryLength,
				agentResultCount: this.currentDisplayedAgentMentions.length,
				peopleResultCount,
				teamResultCount,
			});
			return;
		}
		if (isTeamMentionResult(mention)) {
			const selectedTeamPosition = displayedTeams.findIndex((team) => team.id === mention.id);
			this.mentionAnalyticsCallback?.({
				action: 'selected',
				resultType: 'team',
				position: selectedTeamPosition >= 0 ? selectedTeamPosition : undefined,
				queryLength,
				agentResultCount: this.currentDisplayedAgentMentions.length,
				peopleResultCount,
				teamResultCount,
			});
			return;
		}
		if (!isAgentMention(mention)) {
			return;
		}
		const selectedAgentMentionPosition = this.currentDisplayedAgentMentions.findIndex(
			(agentMention) => agentMention.id === mention.id,
		);
		const selectedAgent = this.currentDisplayedAgents.find(
			(agent) => this.getAgentMentionNodeId(agent) === mention.id,
		);
		// The default Rovo row is synthetic (see `buildRovoMention`) so it never
		// appears in `currentDisplayedAgents`. Attribute it explicitly instead.
		const isDefaultRovo = mention.id === DEFAULT_AGENT_UUID;
		const agentCreatorType =
			selectedAgent?.creatorType ?? (isDefaultRovo ? DEFAULT_AGENT_CREATOR_TYPE : undefined);
		this.mentionAnalyticsCallback?.({
			action: 'selected',
			resultType: 'agent',
			agentId: selectedAgent?.id ?? (isDefaultRovo ? mention.id : undefined),
			agentName: selectedAgent?.name ?? (isDefaultRovo ? mention.name : undefined),
			agentIsVerified: selectedAgent?.isVerified ?? false,
			agentCreatorType,
			position: selectedAgentMentionPosition >= 0 ? selectedAgentMentionPosition : undefined,
			queryLength,
			agentResultCount: this.currentDisplayedAgentMentions.length,
			peopleResultCount,
			teamResultCount,
		});
		if (!this.onAgentSelected) {
			return;
		}
		const avatarUrl = selectedAgent?.avatarUrl ?? mention.avatarUrl;
		this.onAgentSelected({
			id: selectedAgent?.id ?? mention.id,
			name: selectedAgent?.name ?? mention.name ?? '',
			identityAccountId: selectedAgent?.identityAccountId,
			creatorType: agentCreatorType,
			...(avatarUrl ? { avatarUrl } : {}),
		});
	}

	/**
	 * Subscribe to disabled-state changes. Called by `MentionNodeView`
	 * (via the `MentionProvider` interface) so each already-rendered
	 * chip can re-evaluate when the active agent set changes.
	 */
	subscribeToDisabledStateChanges(listener: () => void): () => void {
		this.disabledStateListeners.add(listener);
		return () => {
			this.disabledStateListeners.delete(listener);
		};
	}

	/**
	 * Forwarded to the consumer only when the destroyed id is a known
	 * agent (i.e. present in `getSelectedAgentIds()`). People mentions
	 * are silently ignored so consumers don't need to discriminate.
	 */
	notifyMentionDestroyed(mention: { id: string }): void {
		const ids = this.getSelectedAgentIds?.() ?? [];
		if (!ids.includes(mention.id)) {
			return;
		}
		this.onAgentMentionDestroyed?.(mention.id);
	}

	/**
	 * Notify all subscribers that the disabled-state predicate inputs
	 * have changed (typically because `selectedAgentIds` changed in the
	 * input draft store). Triggers each subscribed `MentionNodeView` to re-run
	 * its `updateState`.
	 */
	notifyDisabledStateChanged(): void {
		this.disabledStateListeners.forEach((listener) => listener());
	}

	setDisabledAgentTooltip(disabledAgentTooltip: string | undefined): void {
		this.disabledAgentTooltip = disabledAgentTooltip;
		this.notifyDisabledStateChanged();
	}

	/**
	 * Agent chip is enabled iff its id is the active agent (`selectedAgentIds.at(-1)`),
	 * otherwise disabled. An empty list means no active agent, so a dismissed
	 * agent's lingering chip stays disabled. Scoped to agent chips via
	 * `userType === 'APP'` (the node carries `userType`, not `appType`).
	 */
	getMentionDisabledState(mention: MentionDisabledStateInput): MentionDisabledState | undefined {
		// Agent chips only — never disable people mentions.
		if (mention.userType !== 'APP') {
			return undefined;
		}

		const ids = this.getSelectedAgentIds?.() ?? [];
		const activeAgentId = ids.at(-1);

		// The active agent is enabled; every other agent chip is disabled.
		if (mention.id === activeAgentId) {
			return undefined;
		}

		return { disabled: true, tooltip: this.disabledAgentTooltip };
	}
}
